import { useState } from 'react'

import { PackageReqFeesResponse } from '@/api/package-req/types/PkgReqFeesResponse'
import { TaxesData } from '@/api/payments/types/TaxesData'
import { Spinner } from '@/components/ui/spinner'
import { ShippingPrices } from '@/helpers/priceChart'

import { ShippingOptions } from '../ShippingPage'
import { PaymentData } from './PaymentData'

interface Props {
  insurancePrice: number
  taxesData: TaxesData | undefined
  delayChargePrice: number
  taxFreeFeeUSD: number
  packageFees: PackageReqFeesResponse | undefined
  paymentData: PaymentData
  shippingOptions: ShippingOptions
  shippingPrices: ShippingPrices
  isLoadingTaxes: boolean
  totalPrice: number
  couponDiscountAmount: number
}
export const PaymentSummaryDetails = ({
  insurancePrice,
  taxesData,
  delayChargePrice,
  taxFreeFeeUSD,
  packageFees,
  paymentData,
  shippingOptions,
  shippingPrices,
  isLoadingTaxes,
  totalPrice,
  couponDiscountAmount,
}: Props) => {
  const [isTaxesDetailsShown, setIsTaxesDetailsShown] = useState(false)

  const shippingPrice = shippingPrices[shippingOptions.selectedShippingMethod]

  return (
    <div className='space-y-4'>
      <h3 className='text-text-primary text-sm font-medium'>סיכום כולל</h3>
      <div className='border-border-default rounded-lg border bg-white p-4'>
        <div className='space-y-3'>
          {/* Shipping */}
          <div className='flex justify-between'>
            <span className='text-text-secondary text-sm'>משלוח</span>
            <span className='text-text-primary text-sm font-medium'>{shippingPrice ? `$${shippingPrice}` : 'לא זמין'}</span>
          </div>

          {/* Delay charge */}
          {!!delayChargePrice && delayChargePrice > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>אחסון</span>
              <span className='text-text-primary text-sm font-medium'>${delayChargePrice}</span>
            </div>
          )}

          {/* Insurance */}
          <div className='flex justify-between'>
            <span className='text-text-secondary text-sm'>ביטוח 360°</span>
            <span className='text-text-primary text-sm font-medium'>${shippingOptions.isInsuranceAdded ? insurancePrice : 0}</span>
          </div>

          {/* Tax Free Fee */}
          {!!taxFreeFeeUSD && taxFreeFeeUSD > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>עלות העברה מדלוואר</span>
              <span className='text-text-primary text-sm font-medium'>${taxFreeFeeUSD}</span>
            </div>
          )}

          {/* Unite Fee */}
          {!!packageFees?.uniteFee && packageFees.uniteFee > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>תוספת בגין איחוד חבילות</span>
              <span className='text-text-primary text-sm font-medium'>${packageFees.uniteFee}</span>
            </div>
          )}

          {/* Photo Request Fee */}
          {!!packageFees?.photoRequestFeeUSD && packageFees.photoRequestFeeUSD > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>עמלת צילום חבילה</span>
              <span className='text-text-primary text-sm font-medium'>${packageFees.photoRequestFeeUSD}</span>
            </div>
          )}

          {/* Shrink Request Fee */}
          {!!packageFees?.shrinkRequestFeeUSD && packageFees.shrinkRequestFeeUSD > 0 && (
            <div className='flex justify-between'>
              <span className='text-text-secondary text-sm'>עמלת צמצום חבילה</span>
              <span className='text-text-primary text-sm font-medium'>${packageFees.shrinkRequestFeeUSD}</span>
            </div>
          )}

          {/* Taxes */}
          <div className='flex justify-between'>
            <span className='text-text-secondary flex items-center gap-1 text-sm'>
              תשלום מיסים מראש
              {taxesData && taxesData.sum > 0 && (
                <button onClick={() => setIsTaxesDetailsShown(!isTaxesDetailsShown)} className='text-text-link hover:underline cursor-pointer'>
                  {isTaxesDetailsShown ? 'הסתר פירוט' : 'הצג פירוט'}
                </button>
              )}
            </span>
            {isLoadingTaxes ? (
              <Spinner />
            ) : (
              <span className='text-text-primary text-sm font-medium'>${taxesData?.sum ? taxesData.sum.toFixed(2) : 0}</span>
            )}
          </div>

          {/* Taxes Details */}
          {isTaxesDetailsShown && taxesData?.details && (
            <div className='border-border-default space-y-2 rounded border bg-gray-50 p-3'>
              <div className='flex justify-between text-xs'>
                <span className='text-text-secondary'>מעמ</span>
                <span className='text-text-primary'>${taxesData.details.vat}</span>
              </div>
              <div className='flex justify-between text-xs'>
                <span className='text-text-secondary'>אגרות</span>
                <span className='text-text-primary'>${taxesData.details.externalFees}</span>
              </div>
              <div className='flex justify-between text-xs'>
                <span className='text-text-secondary'>עמלת שחרור</span>
                <span className='text-text-primary'>${taxesData.details.shiptanbulFee}</span>
              </div>
            </div>
          )}

          {/* Israeli ID Input */}

          {/* Coupon Discount */}
          {couponDiscountAmount > 0 && (
            <div className='flex justify-between text-green-600'>
              <span className='text-sm'>קופון {paymentData.couponCode}:</span>
              <span className='text-sm font-medium'>- {couponDiscountAmount}$</span>
            </div>
          )}

          {/* Divider */}
          <div className='border-border-default border-t' />

          {/* Total */}
          <div className='flex justify-between'>
            <span className='text-text-primary text-base font-medium'>סה״כ:</span>
            <span className='text-text-primary text-base font-medium'>{totalPrice}$</span>
          </div>
        </div>
      </div>
    </div>
  )
}
